import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Send, BookOpen, Brain, HelpCircle, ListChecks, Square,
  Paperclip, Image as ImageIcon, FileText, Mic, X, StopCircle,
  Notebook, History, AudioLines, SquarePen,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useRoleT } from "@/hooks/use-account-type";
import { DsaText } from "@/components/DsaText";
import { AssistantMessage } from "@/components/AssistantMessage";
import { NotesStudio } from "@/components/NotesStudio";
import { ChatHistorySheet } from "@/components/ChatHistorySheet";
import { VoiceTutorPanel } from "@/components/VoiceTutorPanel";
import { LumenMascot } from "@/components/LumenMascot";
import { apiUrl } from "@/lib/api-base";

export const Route = createFileRoute("/_app/ai")({
  component: AiPage,
});

type Attachment = { id: string; kind: "image" | "file" | "audio"; name: string; mediaType: string; dataBase64: string; previewUrl?: string };
type Msg = { role: "user" | "assistant"; content: string; attachments?: Attachment[] };

function AiPage() {
  const { t } = useTranslation();
  const rt = useRoleT();
  const quickPrompts = [
    { icon: BookOpen, label: t("ai.quick.explain"), prompt: t("ai.quick.explainPrompt") },
    { icon: Brain, label: t("ai.quick.summary"), prompt: t("ai.quick.summaryPrompt") },
    { icon: HelpCircle, label: t("ai.quick.quiz"), prompt: t("ai.quick.quizPrompt") },
    { icon: ListChecks, label: t("ai.quick.test"), prompt: t("ai.quick.testPrompt") },
  ];
  const [mode, setMode] = useState<"chat" | "notes" | "voice">("chat");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [recording, setRecording] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Load or create the user's active conversation and hydrate messages
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user || cancelled) return;
      const { data: conv } = await supabase
        .from("chat_conversations")
        .select("id")
        .eq("user_id", u.user.id)
        .eq("mode", "tutor")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      let convId = conv?.id;
      if (!convId) {
        const { data: created } = await supabase
          .from("chat_conversations")
          .insert({ user_id: u.user.id, mode: "tutor", title: "Tutor" })
          .select("id")
          .single();
        convId = created?.id;
      }
      if (!convId || cancelled) return;
      setConversationId(convId);
      const { data: msgs } = await supabase
        .from("chat_messages")
        .select("role,content")
        .eq("conversation_id", convId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (cancelled) return;
      if (msgs && msgs.length > 0) {
        setMessages(
          [...msgs]
            .reverse()
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
        );
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function loadConversation(id: string) {
    setConversationId(id);
    const { data: msgs } = await supabase
      .from("chat_messages")
      .select("role,content")
      .eq("conversation_id", id)
      .order("created_at", { ascending: false })
      .limit(500);
    setMessages(
      [...(msgs ?? [])]
        .reverse()
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
    );
  }

  async function newConversation() {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { data: created, error } = await supabase
      .from("chat_conversations")
      .insert({ user_id: u.user.id, mode: "tutor", title: "Nuova chat" })
      .select("id")
      .single();
    if (error) { toast.error(error.message); return; }
    if (created?.id) {
      setConversationId(created.id);
      setMessages([]);
    }
  }

  async function persistMessage(role: "user" | "assistant", content: string) {
    if (!conversationId || !content.trim()) return;
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    await supabase.from("chat_messages").insert({
      conversation_id: conversationId,
      user_id: u.user.id,
      role,
      content,
    });
    await supabase
      .from("chat_conversations")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", conversationId);
  }

  async function fileToBase64(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    let bin = "";
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(bin);
  }

  async function addFiles(list: FileList | null, kind: "image" | "file") {
    if (!list) return;
    const items: Attachment[] = [];
    for (const f of Array.from(list)) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: max 15MB`); continue; }
      const dataBase64 = await fileToBase64(f);
      items.push({
        id: crypto.randomUUID(),
        kind,
        name: f.name,
        mediaType: f.type || (kind === "image" ? "image/png" : "application/octet-stream"),
        dataBase64,
        previewUrl: kind === "image" ? URL.createObjectURL(f) : undefined,
      });
    }
    setAttachments((a) => [...a, ...items]);
  }

  async function toggleRecord() {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mime });
        const f = new File([blob], `nota-vocale.${mime.includes("mp4") ? "m4a" : "webm"}`, { type: mime });
        const dataBase64 = await fileToBase64(f);
        setAttachments((a) => [...a, {
          id: crypto.randomUUID(), kind: "audio", name: f.name, mediaType: mime, dataBase64,
        }]);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error(t("voice.errors.mic"));
    }
  }

  function removeAttachment(id: string) {
    setAttachments((a) => {
      const it = a.find((x) => x.id === id);
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return a.filter((x) => x.id !== id);
    });
  }

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if ((!content && attachments.length === 0) || loading) return;
    setInput("");
    const userMsg: Msg = { role: "user", content, attachments: attachments.length ? attachments : undefined };
    const next: Msg[] = [...messages, userMsg];
    setMessages(next);
    setAttachments([]);
    setLoading(true);
    setStreaming(true);
    void persistMessage("user", content);
    // Auto-title conversation with the first user message
    if (conversationId && messages.length === 0 && content.trim()) {
      const title = content.trim().slice(0, 60);
      void supabase.from("chat_conversations").update({ title }).eq("id", conversationId);
    }
    // Placeholder assistant message we will fill as tokens arrive
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
        if (!token) throw new Error(t("toast.sessionExpired"));
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const payloadMessages = next.map((m) => ({
        role: m.role,
        content: m.content,
        attachments: m.attachments?.map((a) => ({
          mediaType: a.mediaType, dataBase64: a.dataBase64, name: a.name,
        })),
      }));
      const res = await fetch(apiUrl("/api/chat/stream"), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: payloadMessages }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error(await res.text().catch(() => t("toast.genericError")));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = m.slice();
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
      if (acc.trim()) void persistMessage("assistant", acc);
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        // keep partial text; done
      } else {
      toast.error(e instanceof Error ? e.message : t("toast.genericError"));
        setMessages(next);
      }
    } finally {
      setLoading(false);
      setStreaming(false);
      abortRef.current = null;
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  return (
    <div className="flex h-dvh flex-col">
      <header className="px-5 pt-8 pb-3">
        <div className="flex items-center gap-2">
          <LumenMascot mood="happy" size={40} className="shrink-0" />
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold">{rt("lumen.tutor.title")}</h1>
            <p className="truncate text-xs text-muted-foreground">{rt("lumen.tutor.subtitle")}</p>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("ai.newChat")}
              title={t("ai.newChat")}
              onClick={() => void newConversation()}
              className="h-9 w-9 rounded-xl"
            >
              <SquarePen className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("ai.history")}
              title={t("ai.history")}
              onClick={() => setHistoryOpen(true)}
              className="h-9 w-9 rounded-xl"
            >
              <History className="h-4 w-4" />
            </Button>
          </div>

        </div>
        <p className="mt-1 text-sm text-muted-foreground">{t("ai.subtitle")}</p>
        <div className="mt-3 inline-flex rounded-2xl bg-muted p-1 text-xs font-medium">
          <button
            type="button"
            onClick={() => setMode("chat")}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 transition ${mode === "chat" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
          >
            <Sparkles className="h-3.5 w-3.5" /> {t("ai.chat")}
          </button>
          <button
            type="button"
            onClick={() => setMode("notes")}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 transition ${mode === "notes" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
          >
            <Notebook className="h-3.5 w-3.5" /> {t("ai.notes")}
          </button>
          <button
            type="button"
            onClick={() => setMode("voice")}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 transition ${mode === "voice" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
          >
            <AudioLines className="h-3.5 w-3.5" /> {t("voice.tab")}
          </button>
        </div>
      </header>

      <ChatHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        activeId={conversationId}
        onSelect={(id) => void loadConversation(id)}
        onNew={() => void newConversation()}
      />

      {mode === "voice" ? (
        <div className="flex-1 overflow-y-auto px-5 pb-32">
          <VoiceTutorPanel />
        </div>
      ) : mode === "notes" ? (
        <div className="flex-1 overflow-y-auto px-5 pb-32">
          <NotesStudio />
        </div>
      ) : (
      <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 pb-40">
        {messages.length === 0 ? (
          <div className="mt-4 space-y-2">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("ai.tryAsking")}</p>
            {quickPrompts.map((q) => (
              <button
                key={q.label}
                onClick={() => setInput(q.prompt)}
                className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left text-sm transition hover:bg-muted"
              >
                <div className="rounded-lg bg-primary/10 p-1.5 text-primary"><q.icon className="h-4 w-4" /></div>
                <span className="font-medium">{q.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3 py-3">
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
              >
                {m.role === "user" ? (
                  <div className="max-w-[85%] space-y-2">
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {m.attachments.map((a) => (
                          <div key={a.id} className="overflow-hidden rounded-xl border border-border bg-card">
                            {a.kind === "image" && a.previewUrl ? (
                              <img src={a.previewUrl} alt={a.name} className="h-24 w-24 object-cover" />
                            ) : (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                                {a.kind === "audio" ? <Mic className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                                <span className="max-w-[140px] truncate">{a.name}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {m.content && (
                      <div className="rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-[var(--shadow-soft)]">
                        {m.content}
                      </div>
                    )}
                  </div>
                ) : (
                  <AssistantMessage
                    content={m.content}
                    className="max-w-[92%] text-sm leading-relaxed text-foreground"
                  />
                )}
              </motion.div>
            ))}
            {loading && (
              <div className="flex items-center gap-1 py-2 text-sm text-muted-foreground">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary [animation-delay:150ms]" />
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary [animation-delay:300ms]" />
                <span className="ml-2">{t("ai.thinking")}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed left-0 right-0 z-30 px-5 bottom-[calc(env(safe-area-inset-bottom)+6rem)] pl-[max(env(safe-area-inset-left),1.25rem)] pr-[max(env(safe-area-inset-right),1.25rem)]">
        <div className="mx-auto max-w-md sm:max-w-2xl lg:max-w-4xl">
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5 rounded-2xl bg-card/80 p-2 backdrop-blur">
              {attachments.map((a) => (
                <div key={a.id} className="relative flex items-center gap-1.5 rounded-xl border border-border bg-background px-2 py-1 text-xs">
                  {a.kind === "image" && a.previewUrl ? (
                    <img src={a.previewUrl} alt={`Anteprima allegato ${a.name}`} className="h-8 w-8 rounded object-cover" />
                  ) : a.kind === "audio" ? (
                    <Mic className="h-3.5 w-3.5" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )}
                  <span className="max-w-[120px] truncate">{a.name}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    className="ml-1 rounded-full p-0.5 hover:bg-muted"
                    aria-label="Rimuovi"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            ref={imgInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files, "image"); e.target.value = ""; }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md,.doc,.docx,.csv,application/pdf,text/*"
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files, "file"); e.target.value = ""; }}
          />
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="glass flex items-end gap-2 rounded-3xl p-2 shadow-[var(--shadow-glass)]"
          >
            <div className="flex flex-col gap-1">
              <Button
                type="button" variant="ghost" size="icon"
                aria-label="Allega foto"
                onClick={() => imgInputRef.current?.click()}
                className="h-8 w-8 rounded-xl"
              >
                <ImageIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button" variant="ghost" size="icon"
                aria-label="Allega file"
                onClick={() => fileInputRef.current?.click()}
                className="h-8 w-8 rounded-xl"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
            </div>
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder={t("ai.placeholder")}
              rows={1}
              className="max-h-32 min-h-11 resize-none border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
            />
            <Button
              type="button"
              variant={recording ? "destructive" : "ghost"}
              size="icon"
              aria-label={recording ? "Ferma registrazione" : "Registra audio"}
              onClick={toggleRecord}
              className="h-11 w-11 shrink-0 rounded-2xl"
            >
              {recording ? <StopCircle className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Button
              type="submit"
              size="icon"
              aria-label="Invia"
              disabled={loading || (!input.trim() && attachments.length === 0)}
              className="h-11 w-11 shrink-0 rounded-2xl"
            >
              <Send className="h-4 w-4" />
            </Button>
            {streaming && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Ferma"
                onClick={stop}
                className="h-11 w-11 shrink-0 rounded-2xl"
              >
                <Square className="h-4 w-4" />
              </Button>
            )}
          </form>
        </div>
      </div>
      </>
      )}
    </div>
  );
}